import { FunctionComponent, useEffect } from "react";
import { useState } from "react";
import { connect, useDispatch } from "react-redux";
import { properFormat } from "../../Values/utils";
import Chart from "react-apexcharts";

type props = {
  analyticsData: any;
  heading: string;
  type:
  | "project"
  | "location"
  | "budget"
  | "stage"
  | "propertyType"
  | "propertyStage";
  color: string[];
  GraphType: any;
  style?: any;
  role: any;
  history: any;
  filter: "MTD" | "YTD" | "PM" | "All" | "CUSTOM";
  contacts: any;
  user: any;
  organizationUsers: any[];
  teamLeadUsers: any;
  branchUsers: any;
  theme: any;
};

const IntBarChartOrg: FunctionComponent<props> = ({
  analyticsData,
  heading,
  type,
  color,
  GraphType,
  style,
  role,
  history,
  filter,
  contacts,
  user,
  organizationUsers,
  teamLeadUsers,
  branchUsers,
  theme
}) => {
  const [data, setData] = useState<any>({});
  const [allContacts, setAllContacts] = useState<any[] | undefined>(undefined);
  const [labels, setLabels] = useState<any>([]);
  const [series, setSeries] = useState<any>([]);

  /** --------------------------------------------------------
 * 1. BUILD CONTACT LIST (Not directly used in chart logic)
 * -------------------------------------------------------- */
  useEffect(() => {
    let contact: any[] = [];
    Object.keys(contacts.data).forEach((key) => {
      contact = [...contact, ...contacts.data[key]];
    });
    setAllContacts(contact);
  }, [contacts.data]);

  /** --------------------------------------------------------
 * 2. AGGREGATE ANALYTICS DATA ACROSS ALL USERS
 * -------------------------------------------------------- */
  useEffect(() => {
    const temp: { [key: string]: number } = {};
    Object.values(analyticsData).forEach((analytics: any) => {
      Object.keys(analytics.leadAnalytics[type]).forEach((key) => {
        if (temp[key]) {
          temp[key] += analytics.leadAnalytics[type][key];
        } else {
          temp[key] = analytics.leadAnalytics[type][key];
        }
      });
    });

    // Remove unwanted categories
    delete temp["Pending"];
    delete temp["Completed"];
    const sortable = Object.entries(temp)
      .sort(([, a], [, b]) => b - a)
      .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

    // Sort except for budget
    if (type === "budget") {
      setData(temp);
    }
    else {
      setData(sortable);
    }
  }, [analyticsData]);

  /** --------------------------------------------------------
 * 3. PREPARE LABELS AND SERIES
 * -------------------------------------------------------- */
  useEffect(() => {
    let labels = Object.keys(data).map((item) => properFormat(item));
    let series = Object.values(data);
    setLabels(labels);
    setSeries(series);
  }, [data]);

  /** --------------------------------------------------------
  * 4. DONUT (DEFAULT)
  * -------------------------------------------------------- */
  const donutChartOptions: any = {
    series: series,
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        background: theme ? 'rgba(0, 0, 0, 0)' : 'rgba(26, 43, 60, 1)',
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            customIcons: []
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ',',
              headerCategory: 'category',
              headerValue: 'value',
            },
            svg: {
              filename: undefined,
            },
            png: {
              filename: undefined,
            }
          },
          autoSelected: 'zoom'
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: any) {
          return val.toFixed(0) + "%"
        },
      },
      labels: labels,
      colors: [
        "#71CACC",
        "#87CBAC",
        "#3B6064",
        "#F88377",
        "#272944",
        "#aaf0d1",
        "#ff6961",
        "#93FCF8",
        "#00C9D2",
      ],
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '12px',
        labels: {
          colors: theme ? "#000" : "#fff",
        }
      },
      plotOptions: {
        pie: {
          customScale: 1,
          donut: {
            size: "60%",
          },
          offsetY: 20,
        },
        stroke: {
          colors: undefined,
        },
      }
    },
  };

  /** --------------------------------------------------------
  * 5.  BAR CHART (stage / budget)
  * -------------------------------------------------------- */
  const barChartOptions: any = {
    series: [
      {
        name: "Total",
        data: series,
      },
    ],
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        background: theme ? 'rgba(0, 0, 0, 0)' : 'rgba(26, 43, 60, 1)',
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            customIcons: []
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ',',
              headerCategory: 'category',
              headerValue: 'value',
            },
            svg: {
              filename: undefined,
            },
            png: {
              filename: undefined,
            }
          },
          autoSelected: 'zoom'
        },
      },
      colors: [
        "#71CACC",
        "#87CBAC",
        "#3B6064",
        "#F88377",
        "#272944",
        "#aaf0d1",
        "#ff6961",
        "#93FCF8",
        "#00C9D2",
      ],
      plotOptions: {
        bar: {
          distributed: true,
          horizontal: false,
          columnWidth: "55%",
          endingShape: "rounded",
          dataLabels: {
            position: 'top',
          }
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: theme ? ["#000"] : ["#fff"],
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        categories: labels,
        labels: {
          show: true,
          trim: true,
          style: {
            colors: theme ? "#000" : "#fff",
          },
        },
      },
      yaxis: {
        labels: {
          show: true,
          style: {
            colors: theme ? "#000" : "#fff",
          },
        },
      },
      fill: {
        opacity: 1,
      },

      legend: {
        show: false,
      },
    },
  };

  /** --------------------------------------------------------
  * 6. AREA CHARTS 
  * -------------------------------------------------------- */
  const areaChartOptions: any = {
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        background: theme ? 'rgba(0, 0, 0, 0)' : 'rgba(26, 43, 60, 1)',
        id: "apexchart-example",
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            customIcons: []
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ',',
              headerCategory: 'category',
              headerValue: 'value',
            },
            svg: {
              filename: undefined,
            },
            png: {
              filename: undefined,
            }
          },
          autoSelected: 'zoom'
        }
      },
      xaxis: {
        categories: labels,
        tooltip: {
          enabled: false,
        },
        labels: {
          show: true,
          style: {
            colors: theme ? "#000" : "#fff",
          },
        },
      },
      yaxis: {
        tooltip: {
          enabled: false,
        },
        labels: {
          show: true,
          style: {
            colors: theme ? "#000" : "#fff",
          },
        },
      },
      colors: ["#71CACC"],
    },
    series: [
      {
        name: "Interested",
        data: series,
      },
    ],
  };

  /** --------------------------------------------------------
  * 7. AREA CHARTS 2 
  * -------------------------------------------------------- */
  const areaChartOptions2: any = {
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        background: theme ? 'rgba(0, 0, 0, 0)' : 'rgba(26, 43, 60, 1)',
        id: "apexchart-example",
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            customIcons: []
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ',',
              headerCategory: 'category',
              headerValue: 'value',
            },
            svg: {
              filename: undefined,
            },
            png: {
              filename: undefined,
            }
          },
          autoSelected: 'zoom'
        }
      },
      xaxis: {
        categories: labels,
        tooltip: {
          enabled: false,
        },
        labels: {
          show: true,
          style: {
            colors: theme ? "#000" : "#fff",
          },
        },
      },
      yaxis: {
        tooltip: {
          enabled: false,
        },
        labels: {
          show: true,
          style: {
            colors: theme ? "#000" : "#fff",
          },
        },
      },
      colors: ["#87CBAC"],
    },
    series: [
      {
        name: "Interested",
        data: series,
      },
    ],
  };

  /** --------------------------------------------------------
* 6. Pie CHARTS 
* -------------------------------------------------------- */
  const pieChartOptions: any = {
    series: series,
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        background: theme ? 'rgba(0, 0, 0, 0)' : 'rgba(26, 43, 60, 1)',
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            customIcons: []
          },
          export: {
            csv: {
              filename: undefined,
              columnDelimiter: ',',
              headerCategory: 'category',
              headerValue: 'value',
            },
            svg: {
              filename: undefined,
            },
            png: {
              filename: undefined,
            }
          },
          autoSelected: 'zoom'
        }
      },
      labels: labels,
      dataLabels: {
        enabled: true,
        formatter: function (val: any) {
          return val.toFixed(0) + "%"
        },
      },
      colors: [
        "#71CACC",
        "#87CBAC",
        "#3B6064",
        "#F88377",
        "#272944",
        "#aaf0d1",
        "#ff6961",
        "#93FCF8",
        "#00C9D2",
      ],
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '12px',
        labels: {
          colors: theme ? "#000" : "#fff",
        }
      }
    },
  };


  /** --------------------------------------------------------
   * Responsive width based on series length
   * -------------------------------------------------------- */
  const calculateWidth = () => {
    if (barChartOptions.series[0].data.length > 10) {
      return barChartOptions.series[0].data.length * 100;
    } else {
      return "100%";
    }
  }

  /** --------------------------------------------------------
 * RENDER
 * -------------------------------------------------------- */
  return (
    <>
      {type == "stage" ? (
        <Chart
          options={barChartOptions.options}
          series={barChartOptions.series}
          type="bar"
          width={barChartOptions.series[0].data.length * 100}
          height={280}
        />
      ) : type == "budget" ? (
        <Chart
          options={barChartOptions.options}
          series={barChartOptions.series}
          type="bar"
          width={calculateWidth()}
          height={280}
        />
      ) : type == "location" ? (
        <Chart
          options={areaChartOptions2.options}
          series={areaChartOptions2.series}
          type="area"
          width={calculateWidth()}
          height={250}
        />
      ) : type == "project" ? (
        <Chart
          options={areaChartOptions.options}
          series={areaChartOptions.series}
          type="area"
          width={calculateWidth()}
          height={250}
        />
      ) : type == "propertyStage" ? (
        <Chart
          options={pieChartOptions.options}
          series={pieChartOptions.series}
          type="pie"
          width={"100%"}
          height={280}
        />
      ) : (
        <Chart
          options={donutChartOptions.options}
          series={donutChartOptions.series}
          type="donut"
          width={"100%"}
          height={280}
        />
      )}
    </>
  );
};

/**
 * Maps Redux state to component props.
 */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    role: state.user.role,
    contacts: state.contacts,
    organizationUsers: state.organizationUsers.data,
    teamLeadUsers: state.teamLeadUsers.data,
    branchUsers: state.branchUsers.data,
    theme: state.theme.isLightMode
  };
};
export default connect(mapStateToProps)(IntBarChartOrg);
